/**
 * Authentication Service
 * Handles user authentication, token management, and secure storage
 * 
 * @description This service provides a comprehensive authentication solution with:
 * - Secure JWT token handling
 * - Automatic token refresh
 * - Role-based access control
 * - Session management
 * - Rate limiting compliance
 */

import CryptoJS from 'crypto-js';

// Configuration constants
const API_BASE_URL = process.env.VUE_APP_API_URL || 'http://localhost:3001';
const TOKEN_STORAGE_KEY = 'budget_auth_token';
const USER_STORAGE_KEY = 'budget_user_data';
const REFRESH_TOKEN_KEY = 'budget_refresh_token';
const STORAGE_ENCRYPTION_KEY = process.env.VUE_APP_STORAGE_KEY || 'budget-estimates-secure-key-2025';

// Rate limiting configuration
const LOGIN_ATTEMPT_LIMIT = 5;
const LOGIN_LOCKOUT_DURATION = 15 * 60 * 1000; // 15 minutes

class AuthService {
  constructor() {
    this.isAuthenticated = false;
    this.currentUser = null;
    this.token = null;
    this.refreshToken = null;
    this.loginAttempts = this.getLoginAttempts();
    this.refreshTokenTimer = null;
    
    // Initialize authentication state from storage
    this.initializeFromStorage();
  }

  /**
   * Initialize authentication state from secure storage
   */
  initializeFromStorage() {
    try {
      const token = this.getSecureItem(TOKEN_STORAGE_KEY);
      const userData = this.getSecureItem(USER_STORAGE_KEY);
      const refreshToken = this.getSecureItem(REFRESH_TOKEN_KEY);

      if (token && userData && this.isTokenValid(token)) {
        this.token = token;
        this.currentUser = userData;
        this.refreshToken = refreshToken;
        this.isAuthenticated = true;
        this.setupTokenRefresh();
      } else {
        this.clearAuthData();
      }
    } catch (error) {
      console.error('Failed to initialize auth from storage:', error);
      this.clearAuthData();
    }
  }

  /**
   * Login user with email and password
   * @param {string} email - User email
   * @param {string} password - User password
   * @returns {Promise<Object>} Login result
   */
  async login(email, password) {
    try {
      // Check rate limiting
      if (this.isLoginLocked()) {
        throw new Error('Too many failed login attempts. Please try again later.');
      }

      // Validate input
      if (!email || !password) {
        throw new Error('Email and password are required');
      }

      // Make login request
      const response = await fetch(`${API_BASE_URL}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include', // Include cookies for session handling
        body: JSON.stringify({
          email: this.sanitizeInput(email),
          password: password // Don't sanitize password as it may contain special chars
        })
      });

      const data = await response.json();

      if (!response.ok) {
        // Increment failed login attempts
        this.incrementLoginAttempts();
        throw new Error(data.message || 'Login failed');
      }

      // Reset login attempts on success
      this.resetLoginAttempts();

      // Store authentication data securely
      this.setSecureItem(TOKEN_STORAGE_KEY, data.accessToken);
      this.setSecureItem(USER_STORAGE_KEY, data.user);
      if (data.refreshToken) {
        this.setSecureItem(REFRESH_TOKEN_KEY, data.refreshToken);
      }

      // Update instance state
      this.token = data.accessToken;
      this.currentUser = data.user;
      this.refreshToken = data.refreshToken;
      this.isAuthenticated = true;

      // Setup automatic token refresh
      this.setupTokenRefresh();

      return {
        success: true,
        user: data.user,
        message: 'Login successful'
      };

    } catch (error) {
      console.error('Login error:', error);
      return {
        success: false,
        message: error.message || 'Login failed'
      };
    }
  }

  /**
   * Register new user
   * @param {Object} userData - User registration data
   * @returns {Promise<Object>} Registration result
   */
  async register(userData) {
    try {
      // Validate required fields
      const requiredFields = ['email', 'password', 'name'];
      for (const field of requiredFields) {
        if (!userData[field]) {
          throw new Error(`${field} is required`);
        }
      }

      // Sanitize input data
      const sanitizedData = {
        email: this.sanitizeInput(userData.email),
        name: this.sanitizeInput(userData.name),
        password: userData.password, // Don't sanitize password
        role: userData.role || 'user' // Default role
      };

      const response = await fetch(`${API_BASE_URL}/api/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(sanitizedData)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Registration failed');
      }

      // If registration provides a token, automatically log the user in
      if (data.accessToken) {
        // Store authentication data securely
        this.setSecureItem(TOKEN_STORAGE_KEY, data.accessToken);
        this.setSecureItem(USER_STORAGE_KEY, data.user);
        if (data.refreshToken) {
          this.setSecureItem(REFRESH_TOKEN_KEY, data.refreshToken);
        }

        // Update instance state
        this.token = data.accessToken;
        this.currentUser = data.user;
        this.refreshToken = data.refreshToken;
        this.isAuthenticated = true;

        // Setup automatic token refresh
        this.setupTokenRefresh();
      }

      return {
        success: true,
        message: data.message || 'Registration successful',
        user: data.user,
        autoLogin: !!data.accessToken
      };

    } catch (error) {
      console.error('Registration error:', error);
      return {
        success: false,
        message: error.message || 'Registration failed'
      };
    }
  }

  /**
   * Logout user
   */
  async logout() {
    try {
      // Call logout endpoint to invalidate server-side session
      if (this.token) {
        await fetch(`${API_BASE_URL}/api/auth/logout`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${this.token}`,
            'Content-Type': 'application/json'
          },
          credentials: 'include'
        });
      }
    } catch (error) {
      console.error('Logout API call failed:', error);
    } finally {
      // Clear client-side data regardless of API response
      this.clearAuthData();
    }
  }

  /**
   * Refresh authentication token
   * @returns {Promise<boolean>} Success status
   */
  async refreshAuthToken() {
    try {
      if (!this.refreshToken) {
        throw new Error('No refresh token available');
      }

      const response = await fetch(`${API_BASE_URL}/api/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          refreshToken: this.refreshToken
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Token refresh failed');
      }

      // Update stored tokens
      this.setSecureItem(TOKEN_STORAGE_KEY, data.token);
      if (data.refreshToken) {
        this.setSecureItem(REFRESH_TOKEN_KEY, data.refreshToken);
      }

      this.token = data.token;
      this.refreshToken = data.refreshToken || this.refreshToken;

      return true;

    } catch (error) {
      console.error('Token refresh failed:', error);
      this.clearAuthData();
      return false;
    }
  }

  /**
   * Check if user has required role
   * @param {string|Array} requiredRoles - Required role(s)
   * @returns {boolean} Whether user has permission
   */
  hasRole(requiredRoles) {
    if (!this.isAuthenticated || !this.currentUser) {
      return false;
    }

    const userRole = this.currentUser.role;
    const roles = Array.isArray(requiredRoles) ? requiredRoles : [requiredRoles];
    
    return roles.includes(userRole) || userRole === 'admin';
  }

  /**
   * Get authorization header for API requests
   * @returns {Object} Authorization header
   */
  getAuthHeader() {
    if (!this.token) {
      return {};
    }

    return {
      'Authorization': `Bearer ${this.token}`
    };
  }

  /**
   * Check if token is valid and not expired
   * @param {string} token - JWT token to validate
   * @returns {boolean} Token validity
   */
  isTokenValid(token) {
    try {
      if (!token) return false;

      // Basic JWT structure check
      const parts = token.split('.');
      if (parts.length !== 3) return false;

      // Decode payload (without verification for client-side check)
      const payload = JSON.parse(atob(parts[1]));
      
      // Check expiration
      const currentTime = Math.floor(Date.now() / 1000);
      return payload.exp > currentTime;

    } catch (error) {
      console.error('Token validation error:', error);
      return false;
    }
  }

  /**
   * Setup automatic token refresh
   */
  setupTokenRefresh() {
    if (this.refreshTokenTimer) {
      clearTimeout(this.refreshTokenTimer);
    }

    if (!this.token) return;

    try {
      const payload = JSON.parse(atob(this.token.split('.')[1]));
      const expiresIn = payload.exp * 1000 - Date.now();
      
      // Refresh token 5 minutes before expiration
      const refreshTime = Math.max(expiresIn - 5 * 60 * 1000, 60 * 1000);

      this.refreshTokenTimer = setTimeout(async () => {
        const success = await this.refreshAuthToken();
        if (success) {
          this.setupTokenRefresh();
        }
      }, refreshTime);

    } catch (error) {
      console.error('Failed to setup token refresh:', error);
    }
  }

  /**
   * Clear all authentication data
   */
  clearAuthData() {
    // Clear timers
    if (this.refreshTokenTimer) {
      clearTimeout(this.refreshTokenTimer);
      this.refreshTokenTimer = null;
    }

    // Clear storage
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    localStorage.removeItem(USER_STORAGE_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    sessionStorage.removeItem(TOKEN_STORAGE_KEY);
    sessionStorage.removeItem(USER_STORAGE_KEY);

    // Reset instance state
    this.isAuthenticated = false;
    this.currentUser = null;
    this.token = null;
    this.refreshToken = null;
  }

  /**
   * Securely store sensitive data with encryption
   * @param {string} key - Storage key
   * @param {*} value - Value to store
   */
  setSecureItem(key, value) {
    try {
      const encrypted = CryptoJS.AES.encrypt(
        JSON.stringify(value), 
        STORAGE_ENCRYPTION_KEY
      ).toString();
      localStorage.setItem(key, encrypted);
    } catch (error) {
      console.error('Failed to store secure item:', error);
    }
  }

  /**
   * Retrieve and decrypt sensitive data
   * @param {string} key - Storage key
   * @returns {*} Decrypted value
   */
  getSecureItem(key) {
    try {
      const encrypted = localStorage.getItem(key);
      if (!encrypted) return null;

      const decrypted = CryptoJS.AES.decrypt(encrypted, STORAGE_ENCRYPTION_KEY);
      return JSON.parse(decrypted.toString(CryptoJS.enc.Utf8));
    } catch (error) {
      console.error('Failed to retrieve secure item:', error);
      return null;
    }
  }

  /**
   * Rate limiting helpers
   */
  getLoginAttempts() {
    const attempts = localStorage.getItem('login_attempts');
    return attempts ? JSON.parse(attempts) : { count: 0, timestamp: Date.now() };
  }

  incrementLoginAttempts() {
    const attempts = { count: this.loginAttempts.count + 1, timestamp: Date.now() };
    localStorage.setItem('login_attempts', JSON.stringify(attempts));
    this.loginAttempts = attempts;
  }

  resetLoginAttempts() {
    localStorage.removeItem('login_attempts');
    this.loginAttempts = { count: 0, timestamp: Date.now() };
  }

  // Temporary method to clear rate limiting for testing
  clearRateLimiting() {
    this.resetLoginAttempts();
    console.log('Rate limiting cleared - you can now attempt login');
  }

  isLoginLocked() {
    // In development, be more lenient with rate limiting
    if (process.env.NODE_ENV === 'development') {
      // Allow more attempts in development
      if (this.loginAttempts.count < LOGIN_ATTEMPT_LIMIT * 2) {
        return false;
      }
      // Shorter lockout in development (1 minute instead of 15)
      const timeSinceLastAttempt = Date.now() - this.loginAttempts.timestamp;
      if (timeSinceLastAttempt > 60 * 1000) { // 1 minute
        this.resetLoginAttempts();
        return false;
      }
    } else {
      // Production behavior
      if (this.loginAttempts.count < LOGIN_ATTEMPT_LIMIT) {
        return false;
      }
      
      const timeSinceLastAttempt = Date.now() - this.loginAttempts.timestamp;
      if (timeSinceLastAttempt > LOGIN_LOCKOUT_DURATION) {
        this.resetLoginAttempts();
        return false;
      }
    }
    
    return true;
  }

  /**
   * Input sanitization
   * @param {string} input - Input to sanitize
   * @returns {string} Sanitized input
   */
  sanitizeInput(input) {
    if (typeof input !== 'string') return input;
    
    return input
      .trim()
      .replace(/[<>"'&]/g, '') // Remove potentially dangerous characters
      .substring(0, 255); // Limit length
  }

  /**
   * Get current authentication token
   * @returns {string|null} Current token
   */
  getToken() {
    return this.token;
  }

  /**
   * Get current user data
   * @returns {Object|null} Current user
   */
  getCurrentUser() {
    return this.currentUser;
  }

  /**
   * Check authentication status
   * @returns {boolean} Authentication status
   */
  isLoggedIn() {
    return this.isAuthenticated && this.token && this.isTokenValid(this.token);
  }
}

// Export singleton instance
export default new AuthService();
